import React, {
  forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import { Portal } from '@headlessui/react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from './cn';
import {
  foldVN, tokensOf, matchScore, highlightParts, useListKeyNav, useOutsideClose, usePopupPos,
} from './comboUtils';

/**
 * ComboSelect — ô chọn có TÌM KIẾM, thay thế trực tiếp cho <Select> (viết y hệt: <option>/<optgroup>
 * làm con, onChange nhận sự kiện có `e.target.value`), nên đổi chỗ dùng chỉ là đổi tên thẻ.
 *
 *  - Tìm không dấu, nhiều từ không cần đúng thứ tự ("dong ho" ra "Đồng hồ"); dòng khớp đầu chuỗi lên trước,
 *    phần khớp được tô đậm.
 *  - Bàn phím: ↑ ↓ Home End PageUp PageDown Enter Esc; gõ chữ khi ô đang đóng là mở và tìm luôn.
 *  - Danh sách hiện trong Portal của Headless UI: không bị khung cuộn của hộp thoại cắt, và hộp thoại
 *    coi bấm vào danh sách là "bên trong" (không tự đóng). Hết chỗ phía dưới thì mở lên trên.
 *  - Màn hình hẹp (<640px): danh sách thành tấm nổi đầu màn hình, bám theo vùng nhìn thấy khi bàn phím mở.
 *  - Ô có sẵn dòng value="" (kiểu "Tất cả...") thì có thêm nút × để bỏ chọn nhanh.
 *
 * Props thêm so với <Select>: searchable ('auto' | 'always' | 'never'), clearable, searchPlaceholder,
 * emptyText. 'auto' = hiện ô tìm khi có hơn 7 lựa chọn.
 */

const SEARCH_MIN = 7;     // từ chừng này lựa chọn trở lên mới hiện ô tìm
const RENDER_LIMIT = 200; // số dòng tối đa vẽ cùng lúc — còn lại bảo người dùng gõ thêm để thu hẹp

const BASE =
  'block w-full appearance-none rounded-lg text-sm transition-all duration-200 bg-surface-sunken text-heading border shadow-xs focus:outline-none disabled:cursor-not-allowed disabled:opacity-70';
const STATE = {
  default: 'border-line-strong focus:border-brand focus:ring-1 focus:ring-brand',
  error: 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500',
  success: 'border-emerald-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500',
};

function textOf(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (node.props) return textOf(node.props.children);
  return '';
}

/** <option>/<optgroup>/Fragment → [{ value, label, disabled, group }] */
function parseOptions(children, group, out) {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) { parseOptions(child.props.children, group, out); return; }
    if (child.type === 'optgroup') { parseOptions(child.props.children, child.props.label || null, out); return; }
    if (child.type === 'option') {
      const label = textOf(child.props.children);
      out.push({
        value: child.props.value !== undefined ? String(child.props.value) : label,
        label,
        disabled: !!child.props.disabled,
        group,
      });
    }
  });
  return out;
}

function Highlight({ text, tokens }) {
  if (!tokens.length) return text;
  return highlightParts(text, tokens).map((p, i) => (
    p.hit ? <mark key={i} className="bg-transparent font-semibold text-brand">{p.text}</mark> : <React.Fragment key={i}>{p.text}</React.Fragment>
  ));
}

/**
 * Khung danh sách thả xuống dùng chung (ComboSelect, EmployeeSelect, PartnerSelect...): Portal của Headless UI,
 * vị trí cố định theo `pos` (xem usePopupPos). Màn hình hẹp có thêm lớp mờ phía sau; chạm vào đó là đóng.
 */
export function FloatingPanel({ pos, popupRef, onBackdrop, children }) {
  if (!pos) return null;
  return (
    <Portal>
      <div className="relative z-[70]">
        {pos.mobile ? <div className="fixed inset-0 bg-black/30" aria-hidden="true" onMouseDown={onBackdrop} /> : null}
        <div
          ref={popupRef}
          style={pos.mobile
            ? { position: 'fixed', top: pos.top, left: 8, right: 8, maxHeight: pos.maxH }
            : { position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxH }}
          className="flex flex-col overflow-hidden rounded-xl border border-line bg-surface-raised shadow-lg"
        >
          {children}
        </div>
      </div>
    </Portal>
  );
}

export { Highlight };

export const ComboSelect = forwardRef(function ComboSelect({
  value, onChange, children, placeholder = '-- Chọn --', disabled = false, state = 'default', className,
  name, id, required = false, searchable = 'auto', clearable = true,
  searchPlaceholder = 'Gõ để tìm...', emptyText = 'Không tìm thấy kết quả nào',
  onBlur, onFocus, 'aria-label': ariaLabel, ...rest
}, ref) {
  const autoId = useId();
  const baseId = id || `cs-${autoId.replace(/:/g, '')}`;
  const listId = `${baseId}-list`;
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const typeBuf = useRef({ text: '', t: null });

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const options = parseOptions(children, null, []);
  const cur = value == null ? '' : String(value);
  const emptyOpt = options.find((o) => o.value === '');
  const real = options.filter((o) => o.value !== '');
  const selected = real.find((o) => o.value === cur) || null;
  const showSearch = searchable === 'always' || (searchable !== 'never' && real.length > SEARCH_MIN);
  const tokens = useMemo(() => tokensOf(query), [query]);

  // Dòng hiển thị: có từ khoá → chỉ dòng khớp (xếp theo độ khớp, giữ nhóm); không có → cả danh sách.
  const { rows, items, hidden } = useMemo(() => {
    let src;
    if (tokens.length) {
      const order = new Map();
      real.forEach((o) => { if (!order.has(o.group)) order.set(o.group, order.size); });
      src = real
        .map((o, idx) => ({ o, idx, s: matchScore(o.label, tokens) }))
        .filter((x) => x.s !== null)
        .sort((a, b) => order.get(a.o.group) - order.get(b.o.group) || a.s - b.s || a.idx - b.idx)
        .map((x) => x.o);
    } else {
      src = options;
    }
    const cut = src.length > RENDER_LIMIT ? src.slice(0, RENDER_LIMIT) : src;
    const outRows = [];
    const outItems = [];
    let lastGroup = null;
    cut.forEach((o) => {
      if (o.group && o.group !== lastGroup) outRows.push({ kind: 'group', label: o.group });
      lastGroup = o.group;
      outRows.push({ kind: 'opt', o, i: outItems.length });
      outItems.push(o);
    });
    return { rows: outRows, items: outItems, hidden: src.length - cut.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, tokens]);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const emit = useCallback((v) => {
    if (!onChange) return;
    const target = { value: v, name, id: baseId };
    onChange({ target, currentTarget: target, type: 'change', preventDefault() {}, stopPropagation() {} });
  }, [onChange, name, baseId]);

  const close = useCallback((refocus) => {
    setOpen(false);
    setQuery('');
    if (refocus && triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  }, []);

  const pick = useCallback((i) => {
    const o = itemsRef.current[i];
    if (!o || o.disabled) return;
    emit(o.value);
    close(true);
  }, [emit, close]);

  const openList = useCallback((seed) => {
    if (disabled) return;
    setQuery(seed || '');
    setOpen(true);
  }, [disabled]);

  const { active, setActive, onKeyDown: navKey, itemRef } = useListKeyNav({
    open,
    count: items.length,
    isDisabled: (i) => !!items[i]?.disabled,
    onPick: pick,
    onClose: close,
    onOpen: () => openList(''),
  });

  // Mở ra → đặt dòng đang chọn là dòng hiện tại; đổi từ khoá → nhảy về kết quả đầu.
  useEffect(() => {
    if (!open) return;
    const list = itemsRef.current;
    if (tokens.length) {
      setActive(list.findIndex((o) => !o.disabled));
    } else {
      const s = list.findIndex((o) => o.value === cur && !o.disabled);
      setActive(s >= 0 ? s : list.findIndex((o) => !o.disabled));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tokens]);

  // ---- vị trí danh sách ----
  const pos = usePopupPos(open, triggerRef, popupRef);

  useOutsideClose(open, [wrapRef, popupRef], () => close(false));

  // Ô chọn bị khoá giữa chừng (hoặc gỡ khỏi màn hình) thì đóng danh sách.
  useEffect(() => { if (disabled && open) close(false); }, [disabled, open, close]);

  // Focus: ô tìm nếu có, không thì chính khung danh sách (để nhận phím). Callback ref ổn định → chỉ chạy lúc gắn.
  const setInputEl = useCallback((el) => { inputRef.current = el; if (el) el.focus({ preventScroll: true }); }, []);
  const setListEl = useCallback((el) => { listRef.current = el; if (el && !inputRef.current) el.focus({ preventScroll: true }); }, []);

  // Gõ chữ khi ô đóng (hoặc mở mà không có ô tìm): nhảy tới dòng có nhãn bắt đầu bằng chữ vừa gõ.
  const typeAhead = (ch) => {
    const b = typeBuf.current;
    clearTimeout(b.t);
    b.text += ch;
    b.t = setTimeout(() => { b.text = ''; }, 700);
    const want = foldVN(b.text);
    const list = itemsRef.current;
    const at = list.findIndex((o) => !o.disabled && foldVN(o.label).startsWith(want));
    if (at >= 0) setActive(at);
  };

  const onTriggerKey = (e) => {
    if (e.nativeEvent && (e.nativeEvent.isComposing || e.keyCode === 229)) return;
    if (!open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== ' ') {
      e.preventDefault();
      if (showSearch) { openList(e.key); } else { openList(''); setTimeout(() => typeAhead(e.key), 0); }
      return;
    }
    if (e.key === ' ' && !open) return; // để nút tự xử lý (click)
    navKey(e);
  };

  const onPopupKey = (e) => {
    if (e.nativeEvent && (e.nativeEvent.isComposing || e.keyCode === 229)) return;
    if (!showSearch && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      typeAhead(e.key);
      return;
    }
    navKey(e);
  };

  const label = selected ? selected.label : (emptyOpt && emptyOpt.label) || placeholder;
  const isPlaceholder = !selected;
  const canClear = clearable && !!emptyOpt && !!selected && !disabled && !required;

  const setTriggerRef = (el) => {
    triggerRef.current = el;
    if (typeof ref === 'function') ref(el); else if (ref) ref.current = el;
  };

  const popup = open && pos ? (
    <FloatingPanel pos={pos} popupRef={popupRef} onBackdrop={() => close(false)}>
      {showSearch ? (
        <div className="shrink-0 border-b border-line p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" />
            <input
              ref={setInputEl}
              type="text"
              role="searchbox"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onPopupKey}
              placeholder={searchPlaceholder}
              aria-label="Tìm trong danh sách"
              aria-controls={listId}
              aria-activedescendant={active >= 0 ? `${baseId}-o${active}` : undefined}
              className="w-full rounded-lg border border-line bg-surface py-2 pl-8 pr-8 text-sm text-heading placeholder:text-muted focus:border-brand focus:outline-none"
            />
            {query ? (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                aria-label="Xoá từ khoá"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:text-heading"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div
        ref={setListEl}
        id={listId}
        role="listbox"
        tabIndex={-1}
        aria-label={ariaLabel || undefined}
        onKeyDown={onPopupKey}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1 focus:outline-none"
      >
        {items.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-muted">{emptyText}</div>
        ) : rows.map((r, n) => {
          if (r.kind === 'group') {
            return <div key={`g${n}`} role="presentation" className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted">{r.label}</div>;
          }
          const on = r.o.value === cur;
          const hot = r.i === active;
          return (
            <div
              key={`${r.o.value}-${r.i}`}
              ref={itemRef(r.i)}
              id={`${baseId}-o${r.i}`}
              role="option"
              aria-selected={on}
              aria-disabled={r.o.disabled || undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r.i)}
              onMouseMove={() => { if (!r.o.disabled && active !== r.i) setActive(r.i); }}
              className={cn(
                'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm sm:py-1.5',
                r.o.disabled ? 'cursor-not-allowed text-muted opacity-60' : (on ? 'text-brand' : 'text-body'),
                hot && !r.o.disabled && 'bg-surface-sunken',
                on && 'font-medium',
                r.o.value === '' && 'italic text-muted'
              )}
            >
              <span className="min-w-0 flex-1 break-words">
                <Highlight text={r.o.label} tokens={tokens} />
              </span>
              {on ? <Check className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" /> : null}
            </div>
          );
        })}
        {hidden > 0 ? (
          <div className="px-3 py-2 text-center text-xs text-muted">Còn {hidden} kết quả nữa — gõ thêm để thu hẹp</div>
        ) : null}
      </div>
    </FloatingPanel>
  ) : null;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        {...rest}
        ref={setTriggerRef}
        id={baseId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? close(false) : openList(''))}
        onKeyDown={onTriggerKey}
        onFocus={onFocus}
        onBlur={onBlur}
        className={cn(
          BASE, STATE[state] || STATE.default,
          'text-left pl-3 py-2.5 truncate',
          canClear ? 'pr-16' : 'pr-9',
          !disabled && 'cursor-pointer',
          isPlaceholder && 'text-muted',
          className
        )}
      >
        {label || ' '}
      </button>
      {canClear ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Bỏ chọn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { emit(''); }}
          className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:bg-surface-raised hover:text-heading"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
      <ChevronDown
        className={cn('pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted transition-transform', open && 'rotate-180')}
        aria-hidden="true"
      />
      {required ? (
        // Ô ẩn để trình duyệt vẫn chặn gửi form khi chưa chọn (nút bấm không có `required`).
        <input
          tabIndex={-1}
          aria-hidden="true"
          required
          value={cur}
          onChange={() => {}}
          onFocus={() => triggerRef.current && triggerRef.current.focus()}
          className="pointer-events-none absolute bottom-0 left-1/2 h-px w-px opacity-0"
        />
      ) : null}
      {popup}
    </div>
  );
});

export default ComboSelect;
